// Empty-state icons — light blue, static, no animation, no cats.
// - type="received" (default): envelope with a letter tucked inside
// - type="sent": paper airplane — top-down view with two symmetric wings
//   meeting at a central fold, a V-cut tail split at the trailing edge,
//   and a prominent center spine. This is the mental image everyone has
//   when they picture a paper plane you fold in class.
//
// Feb 2026 v5: went back to the classic symmetric top-down silhouette
// (nose right, two wings sweeping back, visible fold line and V-cut tail)
// because the previous "send-icon polygon" style still didn't read as
// paper enough. Added a bright center fold + white edge-highlights so the
// paper look is unmistakable.
import { memo } from "react";

const BLUE_LIGHT = "#DCEEFF";
const BLUE = "#A8D8FF";
const BLUE_DEEP = "#7CB8E6";
const BLUE_INK = "#3F7BB5";
const CREAM = "#FFF8EC";

function EnvelopeIcon({ S }) {
  return (
    <svg viewBox="0 0 200 200" width={S} height={S} aria-hidden>
      <ellipse cx="100" cy="176" rx="62" ry="5" fill={BLUE_DEEP} opacity="0.18" />
      <rect x="30" y="80" width="140" height="90" rx="6" fill={BLUE_LIGHT} stroke={BLUE_INK} strokeWidth="1.5" />
      <rect x="30" y="80" width="140" height="4" fill={BLUE_DEEP} opacity="0.15" />
      <g>
        <rect x="52" y="34" width="96" height="100" rx="3" fill={CREAM} stroke={BLUE_INK} strokeWidth="1.3" />
        <line x1="64" y1="52" x2="136" y2="52" stroke={BLUE_DEEP} strokeWidth="2.4" strokeLinecap="round" />
        <line x1="64" y1="63" x2="132" y2="63" stroke={BLUE_DEEP} strokeWidth="1.8" strokeLinecap="round" opacity="0.75" />
        <line x1="64" y1="74" x2="122" y2="74" stroke={BLUE_DEEP} strokeWidth="1.8" strokeLinecap="round" opacity="0.55" />
      </g>
      <path
        d="M 30 80 L 100 130 L 170 80 L 170 170 L 30 170 Z"
        fill={BLUE} stroke={BLUE_INK} strokeWidth="1.5" strokeLinejoin="round"
      />
      <path d="M 30 170 L 100 138 L 170 170" stroke={BLUE_INK} strokeWidth="1" fill="none" opacity="0.4" />
      <path d="M 32 82 L 100 132 L 168 82" stroke={BLUE_DEEP} strokeWidth="1.1" fill="none" opacity="0.55" />
      <path d="M 36 88 L 36 164" stroke="white" strokeWidth="1.6" strokeLinecap="round" opacity="0.4" />
    </svg>
  );
}

function AirplaneIcon({ S }) {
  // Classic paper airplane silhouette, top-down view, nose pointing right.
  //   Nose:        (188, 100)  — sharp tip on the right
  //   Upper wingtip: (18, 42)   — trailing edge of the LEFT wing (as flying)
  //   Lower wingtip: (18, 158)  — trailing edge of the RIGHT wing
  //   Fold apex:    (72, 100)   — where both wings meet at the crease
  //   Tail cut-out is the V from (18,42) → (72,100) → (18,158)
  // Adding an inner darker triangle behind the fold gives the illusion of
  // the paper folding down and casting a shadow along the crease.
  return (
    <svg viewBox="0 0 200 200" width={S} height={S} aria-hidden>
      {/* Ground shadow, offset to the left to sell rightward flight */}
      <ellipse cx="88" cy="182" rx="52" ry="4" fill={BLUE_DEEP} opacity="0.22" />

      {/* Motion trail — three soft dashes trailing behind */}
      <g opacity="0.6">
        <path d="M 32 60  L 8 55"  stroke={BLUE_DEEP} strokeWidth="2.2" strokeLinecap="round" />
        <path d="M 28 100 L 4 100" stroke={BLUE_DEEP} strokeWidth="2.2" strokeLinecap="round" opacity="0.75" />
        <path d="M 32 140 L 8 145" stroke={BLUE_DEEP} strokeWidth="2.2" strokeLinecap="round" opacity="0.5" />
      </g>

      {/* LOWER wing (right wing from the plane's POV) — drawn first so the
          upper wing overlaps it slightly at the fold for depth. Filled with
          the darker shade because it visually recedes. */}
      <path
        d="M 188 100 L 18 158 L 72 100 Z"
        fill={BLUE_DEEP}
        stroke={BLUE_INK}
        strokeWidth="1.8"
        strokeLinejoin="round"
      />

      {/* UPPER wing (left wing from the plane's POV) — the brighter face */}
      <path
        d="M 188 100 L 18 42 L 72 100 Z"
        fill={BLUE}
        stroke={BLUE_INK}
        strokeWidth="1.8"
        strokeLinejoin="round"
      />

      {/* Center crease — thick spine from fold apex straight to the nose.
          Draw as two stacked lines: dark ink underneath, white highlight
          on top → reads unmistakably as a folded paper edge. */}
      <path
        d="M 72 100 L 188 100"
        stroke={BLUE_INK}
        strokeWidth="2.6"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M 75 98.5 L 184 99"
        stroke="white"
        strokeWidth="1"
        strokeLinecap="round"
        fill="none"
        opacity="0.7"
      />

      {/* Tail V-notch — subtle inner line showing the paper edges meeting */}
      <path
        d="M 18 42 L 72 100 L 18 158"
        stroke={BLUE_INK}
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        opacity="0.5"
      />

      {/* Highlight streak along the upper wing to sell the paper texture */}
      <path
        d="M 44 55 L 174 96"
        stroke="white"
        strokeWidth="1.6"
        strokeLinecap="round"
        opacity="0.4"
      />

      {/* Nose sparkle */}
      <circle cx="188" cy="100" r="3.2" fill="white" opacity="0.95" />
      <circle cx="186" cy="101" r="1.2" fill={CREAM} opacity="0.9" />
    </svg>
  );
}

const PaperPlaneEmpty = memo(function PaperPlaneEmpty({
  size = 200,
  variant, // legacy prop — ignored
  type,    // "received" | "sent"
}) {
  const resolved = type === "sent" ? "sent" : "received";
  return (
    <div
      className="relative mx-auto block"
      style={{ width: size, height: size }}
      aria-hidden
      data-testid="empty-illustration"
      data-empty-type={resolved}
    >
      {resolved === "sent" ? <AirplaneIcon S={size} /> : <EnvelopeIcon S={size} />}
    </div>
  );
});

export default PaperPlaneEmpty;
